import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { clearApiCache } from '../lib/api';

interface Permission { resource: string; action: string; }
interface Role { id: string; name: string; label: string; permissions: Permission[]; }
interface UserData {
  id: string; email: string; name: string; status: string;
  tenantId: string; roleId?: string;
  role?: Role; tenant?: { id: string; name: string; plan: string };
}

interface AuthContextType {
  user: UserData | null;
  isAuthenticated: boolean;
  tenantId: string;
  login: (userData: any) => void;
  logout: () => void;
  setTenantId: (id: string) => void;
  can: (resource: string, action: string) => boolean;
  hasRole: (...roles: string[]) => boolean;
  isOwner: boolean;
  isAdmin: boolean;
}

const AuthContext = createContext<AuthContextType>(null!);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserData | null>(() => {
    try { return JSON.parse(sessionStorage.getItem('user') || 'null'); } catch { return null; }
  });
  const [tenantId, setTenantIdState] = useState(() =>
    sessionStorage.getItem('tenantId') || 'alpha-tenant-id'
  );

  const login = useCallback((userData: any, token?: string) => {
    sessionStorage.setItem('user', JSON.stringify(userData));
    if (token) {
      sessionStorage.setItem('auth_token', token);
    }
    if (userData.tenantId) {
      sessionStorage.setItem('tenantId', userData.tenantId);
      setTenantIdState(userData.tenantId);
    }
    setUser(userData);
  }, []);

  const logout = useCallback(() => {
    sessionStorage.removeItem('user');
    sessionStorage.removeItem('tenantId');
    sessionStorage.removeItem('auth_token');
    clearApiCache();
    setUser(null);
    window.location.hash = '#';
  }, []);

  const setTenantId = useCallback((id: string) => {
    sessionStorage.setItem('tenantId', id);
    setTenantIdState(id);
  }, []);

  const can = useCallback((resource: string, action: string): boolean => {
    if (!user?.role?.permissions) return true; // Legacy user = allow all
    const roleName = user.role.name;
    if (roleName === 'owner' || roleName === 'admin') return true;
    return user.role.permissions.some(p => p.resource === resource && p.action === action);
  }, [user]);

  const hasRole = useCallback((...roles: string[]): boolean => {
    if (!user?.role) return true; // Legacy user
    return roles.includes(user.role.name);
  }, [user]);

  const isOwner = !user?.role || user?.role?.name === 'owner';
  const isAdmin = !user?.role || user?.role?.name === 'admin' || isOwner;

  return (
    <AuthContext.Provider value={{
      user, isAuthenticated: !!user, tenantId,
      login, logout, setTenantId,
      can, hasRole, isOwner, isAdmin
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
